'use client';

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock } from 'lucide-react';
import SectionTitle from '@/components/ui/SectionTitle';
import { api } from '@/utils/api';
import { accentHexForFormato, rgbaFromHex } from '@/constants/obras-generos';
import AtualizadoObraCard, { type AtualizadoObraCardObra as ObraItem } from './AtualizadoObraCard';

const LIMIT = 21;
/** Mobile: 14 obras, sempre 2 por linha (`grid-cols-2`). */
const MOBILE_DISPLAY_LIMIT = 14;

function ensureList(res: unknown): ObraItem[] {
    if (res == null) return [];
    if (Array.isArray(res)) {
        return res.filter(
            (o): o is ObraItem => o != null && typeof o === 'object' && 'id' in o && typeof (o as ObraItem).id === 'number',
        );
    }
    if (typeof res === 'object' && 'data' in (res as object)) return ensureList((res as { data: unknown }).data);
    if (typeof res === 'object' && 'items' in (res as object)) return ensureList((res as { items: unknown }).items);
    if (typeof res === 'object' && 'obras' in (res as object)) return ensureList((res as { obras: unknown }).obras);
    return [];
}

function normalizeAtualizadaItem(input: ObraItem & Record<string, unknown>): ObraItem {
    const ultimos = Array.isArray(input.ultimos_capitulos)
        ? (input.ultimos_capitulos as Array<Record<string, unknown>>).map((c) => ({
              id: Number(c.cap_id ?? 0) || undefined,
              numero: typeof c.cap_num === 'number' ? c.cap_num : undefined,
              nome: typeof c.cap_nome === 'string' ? c.cap_nome : undefined,
              cap_lancado_em: c.publicado_em,
          }))
        : [];
    const totalCap =
        typeof input.total_capitulos === 'number'
            ? input.total_capitulos
            : typeof input.total_capitulos_ativos === 'number'
              ? input.total_capitulos_ativos
              : undefined;
    return {
        ...input,
        capitulos: input.capitulos ?? ultimos,
        ...(totalCap !== undefined ? { total_capitulos: totalCap } : {}),
    };
}

function AtualizadosMobileSkeletonCells({ count }: { count: number }) {
    return (
        <div className="grid grid-cols-2 gap-3 pb-2 sm:gap-4">
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="flex min-w-0 flex-col gap-2 p-0.5">
                    <div className="aspect-[2/3] w-full animate-pulse rounded-2xl bg-white/[0.08]" />
                    <div className="h-4 w-full animate-pulse rounded bg-white/[0.06]" />
                    <div className="h-4 w-3/4 animate-pulse rounded bg-white/[0.06]" />
                    <div className="h-10 w-full animate-pulse rounded-lg bg-white/[0.06]" />
                    <div className="h-10 w-full animate-pulse rounded-lg bg-white/[0.06]" />
                </div>
            ))}
        </div>
    );
}

/** Skeleton alinhado à grelha desktop (7 colunas). */
function AtualizadosGridSkeleton({ cols = 7 }: { cols?: number }) {
    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-7 md:gap-2 xl:gap-2.5">
            {Array.from({ length: cols }).map((_, i) => (
                <div key={i} className="flex min-w-0 flex-col gap-2">
                    <div className="aspect-[2/3] w-full animate-pulse rounded-2xl bg-white/[0.08]" />
                    <div className="h-4 w-full animate-pulse rounded bg-white/[0.06]" />
                    <div className="h-4 w-3/4 animate-pulse rounded bg-white/[0.06]" />
                    <div className="h-10 w-full animate-pulse rounded-lg bg-white/[0.06]" />
                    <div className="h-10 w-full animate-pulse rounded-lg bg-white/[0.06]" />
                </div>
            ))}
        </div>
    );
}

interface AtualizadosRecentesProps {
    /** Formato (gênero) id — vem da home / `?formato=`. */
    formatoId?: number;
}

export default function AtualizadosRecentes({ formatoId = 5 }: AtualizadosRecentesProps) {
    const navigate = useNavigate();
    const [obras, setObras] = useState<ObraItem[]>([]);
    const [loading, setLoading] = useState(true);
    const accent = accentHexForFormato(formatoId);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        void api
            .get(`obras/atualizadas-recentes?limit=${LIMIT}&offset=0&formato=${formatoId}`)
            .then((res) => {
                const raw = res.data;
                const items = (raw as { items?: unknown })?.items;
                const list = ensureList(items ?? raw).map((o) =>
                    normalizeAtualizadaItem(o as ObraItem & Record<string, unknown>),
                );
                if (!cancelled) setObras(list);
            })
            .catch(() => {
                if (!cancelled) setObras([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [formatoId]);

    const list = Array.isArray(obras) ? obras : [];

    if (!loading && list.length === 0) return null;

    return (
        <section data-section="atualizados-recentes" className="relative py-4 md:py-8">
            <div className="relative mb-6 flex items-center justify-between gap-4">
                <SectionTitle icon={Clock} accentColor={accent}>
                    Atualizados Recentes
                </SectionTitle>
            </div>

            {loading && (
                <div className="space-y-6 pb-2">
                    <div className="md:hidden">
                        <AtualizadosMobileSkeletonCells count={MOBILE_DISPLAY_LIMIT} />
                    </div>
                    <div className="hidden md:block">
                        <AtualizadosGridSkeleton cols={7} />
                    </div>
                    <div className="flex justify-center pb-2">
                        <div className="h-10 w-52 animate-pulse rounded-full bg-white/[0.08]" aria-hidden />
                    </div>
                </div>
            )}

            {!loading && (
                <>
                    {/* Mobile — 2 colunas, limite curto (igual app em viewport estreita). */}
                    <div className="md:hidden">
                        <div className="grid grid-cols-2 gap-3 pb-4 sm:gap-4">
                            {list.slice(0, MOBILE_DISPLAY_LIMIT).map((obra) => (
                                <div key={obra.id} className="min-w-0 overflow-visible p-0.5">
                                    <AtualizadoObraCard obra={obra} formatoId={formatoId} coverLoading="eager" />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Desktop — 7 colunas, cartões compactos. */}
                    <div className="hidden md:block">
                        <div className="grid grid-cols-7 gap-2 xl:gap-2.5">
                            {list.slice(0, LIMIT).map((obra) => (
                                <div key={obra.id} className="min-w-0 overflow-visible p-0.5">
                                    <AtualizadoObraCard obra={obra} formatoId={formatoId} compact coverLoading="eager" />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-6 flex flex-col items-center gap-4 pb-2 md:pb-0">
                        <button
                            type="button"
                            onClick={() => navigate(`/atualizados?formato=${formatoId}&pagina=1`)}
                            className="cursor-pointer rounded-full border px-6 py-2.5 text-sm font-medium transition-all hover:brightness-110"
                            style={{
                                borderColor: rgbaFromHex(accent, 0.45),
                                color: accent,
                                backgroundColor: rgbaFromHex(accent, 0.08),
                            }}
                        >
                            Ver todos atualizados
                        </button>
                    </div>
                </>
            )}
        </section>
    );
}
